"use client";
import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Trimbakeshwar phrase overlays — top-left, yellow-prominent, per-word colour + line breaks,
// black drop-shadow (no outline), scattered-holes distress mask, word-by-word drop-in + hold.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
// black DROP SHADOW (no outline)
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;      // enough box for headline/matras (mask won't clip) but tight
const LINE_GAP = -10; // pull stacked lines closer together (px)

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// Part A (0–86.45s) — the hook
export const PART_A: Phrase[] = [
  { start: 6.0, out: 9.4, lines: [
    [{ t: "पाताल", c: "w", big: true }],
    [{ t: "की" }, { t: "ओर" }, { t: "धंसा" }],
    [{ t: "हुआ" }, { t: "है" }],
  ]},
  { start: 30.5, out: 33.9, lines: [
    [{ t: "शिव" }, { t: "ब्रह्मा" }, { t: "और", c: "r", small: true }, { t: "विष्णु" }],
  ]},
  { start: 40.9, out: 43.8, lines: [
    [{ t: "खौफनाक" }, { t: "साज़िश", c: "w", big: true }],
  ]},
  { start: 47.3, out: 50.3, lines: [
    [{ t: "इक्कीस", c: "w", big: true }, { t: "फ़ीट", c: "w", big: true }, { t: "गहरा" }],
  ]},
  { start: 56.0, out: 59.8, lines: [
    [{ t: "श्रापित" }],
    [{ t: "नासक", c: "w", big: true }],
    [{ t: "हीरा" }],
  ]},
];

// Part B — story half. Times are RELATIVE to the Part B start (original − 86.445s).
export const PART_B: Phrase[] = [
  { start: 15.3,  out: 18.6, lines: [[{ t: "सौ" }, { t: "साल" }, { t: "का" }], [{ t: "भयानक" }, { t: "अकाल", c: "w", big: true }]] },
  { start: 145.9, out: 149.3, lines: [[{ t: "हत्यारा!", c: "r", big: true }], [{ t: "गौ-हत्यारा!", c: "r", big: true }]] },
  { start: 189.2, out: 192.6, lines: [[{ t: "एक", c: "w", big: true }, { t: "करोड़", c: "w", big: true }], [{ t: "मिट्टी" }, { t: "के" }, { t: "शिवलिंग" }]] },
  { start: 194.5, out: 197.6, lines: [[{ t: "एक", c: "w", big: true }, { t: "करोड़", c: "w", big: true }], [{ t: "शिवलिंग" }]] },
  { start: 253.4, out: 256.6, lines: [[{ t: "गौतम" }], [{ t: "निर्दोष", c: "w", big: true }, { t: "हैं" }]] },
  { start: 268.1, out: 271.4, lines: [[{ t: "साक्षात" }], [{ t: "महादेव", c: "w", big: true }, { t: "के", small: true }, { t: "दर्शन" }]] },
  { start: 283.7, out: 287.0, lines: [[{ t: "वो" }, { t: "स्वयं" }], [{ t: "देवी" }, { t: "गंगा", c: "w", big: true }, { t: "थीं" }]] },
  { start: 314.2, out: 317.4, lines: [[{ t: "अट्ठाईसवें", c: "w", big: true }, { t: "मनु", c: "w", big: true }, { t: "तक" }]] },
  { start: 330.9, out: 334.0, lines: [[{ t: "अरबों", c: "w", big: true }, { t: "सालों" }, { t: "तक" }]] },
  { start: 340.6, out: 343.9, lines: [[{ t: "तीन" }, { t: "आँखों" }, { t: "वाले" }], [{ t: "भगवान", c: "w", big: true }]] },
  { start: 547.4, out: 550.6, lines: [[{ t: "नासक", c: "w", big: true }], [{ t: "हीरा" }]] },
  { start: 568.9, out: 572.0, lines: [[{ t: "हीरे" }, { t: "का" }, { t: "असली" }], [{ t: "श्राप", c: "r", big: true }]] },
  { start: 601.7, out: 605.0, lines: [[{ t: "गुप्त" }], [{ t: "गुफा", c: "w", big: true }]] },
  { start: 667.6, out: 671.0, lines: [[{ t: "साठ", c: "w", big: true }, { t: "हज़ार", c: "w", big: true }], [{ t: "साल!" }]] },
  { start: 674.0, out: 677.2, lines: [[{ t: "सिंहस्थ" }], [{ t: "कुंभ", c: "w", big: true }]] },
  { start: 757.8, out: 761.1, lines: [[{ t: "काल", c: "r", big: true }, { t: "सर्प", c: "r", big: true }], [{ t: "दोष", c: "r", big: true }]] },
  { start: 802.7, out: 806.0, lines: [[{ t: "हवा" }, { t: "में" }], [{ t: "तैरते", c: "w", big: true }]] },
  { start: 832.0, out: 837.0, lines: [[{ t: "जय" }, { t: "त्र्यंबकराज!", c: "w", big: true }], [{ t: "हर" }, { t: "हर" }, { t: "महादेव!", c: "w", big: true }]] },
];

const PHRASES = PART_A;

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : LINE_GAP }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: LH,
                letterSpacing: "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ── Opener title card — center-aligned, bigger font, whole-screen fade in/out.
// Reuses the EXACT same holes mask + font + colours + drop-shadow as the phrase overlays.
export const TrimbakeshwarOpenerTitle: React.FC<{
  text?: string;
  startS?: number;   // when fade-in begins
  endS?: number;     // when fade-out completes
  fadeInS?: number;
  fadeOutS?: number;
  fontSize?: number;
  color?: "y" | "w" | "r";
}> = ({
  text = "जय त्र्यंबकेश्वर! 🚩",
  startS = 16.0,
  endS = 18.8,
  fadeInS = 0.6,
  fadeOutS = 0.6,
  fontSize = 104,
  color = "y",
}) => {
  const { fps } = useVideoConfig();
  const frame = useCurrentFrame();
  const t = frame / fps;
  const op = interpolate(
    t,
    [startS, startS + fadeInS, endS - fadeOutS, endS],
    [0, 1, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  if (op <= 0) return null;
  return (
    <AbsoluteFill style={{
      backgroundColor: "transparent",
      WebkitMaskImage: `url(${HOLES})`,
      maskImage: `url(${HOLES})`,
      WebkitMaskSize: "1280px 704px",
      maskSize: "1280px 704px",
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "0 0",
      maskPosition: "0 0",
    }}>
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", opacity: op }}>
        <span style={{
          fontFamily: `${fontFamily}, "Noto Color Emoji"`,
          color: col(color),
          fontSize,
          fontWeight: 900,
          lineHeight: 1.18,
          letterSpacing: "0.005em",
          textShadow: SH,
          textAlign: "center",
          padding: "0 40px",
          whiteSpace: "nowrap",
        }}>{text}</span>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

export const TrimbakeshwarOverlays: React.FC<{ phrases?: Phrase[] }> = ({ phrases = PHRASES }) => {
  const { fps } = useVideoConfig();
  return (
    // ONE full-frame holes mask over the whole overlay — opaque everywhere except the
    // scattered holes, so it never clips glyph edges or shadows (no box boundary).
    <AbsoluteFill style={{
      backgroundColor: "transparent",
      WebkitMaskImage: `url(${HOLES})`,
      maskImage: `url(${HOLES})`,
      WebkitMaskSize: "1280px 704px",
      maskSize: "1280px 704px",
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "0 0",
      maskPosition: "0 0",
    }}>
      {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
